import type { QueryClient } from "@tanstack/react-query";
import { createRoute, lazyRouteComponent, redirect } from "@tanstack/react-router";
import { requireLocationManager } from "domains/auth/access/locationManager";
import { connectSearchSchema } from "domains/connect/routing";
import { queries as limitedQueries } from "domains/connect/views/Limited/queries";
import { hasJobsAccess } from "domains/hire/access/jobsAccess";
import { queries as candidatesQueries } from "domains/hire/containers/Candidates/queries";
import { candidatesSearchSchema } from "domains/hire/containers/Candidates/routing";
import { directOfferSearchSchema } from "domains/pros/containers/SendDirectOfferDialog/routing";
import { queries as connectQueries } from "domains/pros/views/Connect/queries";
import { queries as manageNetworkQueries } from "domains/pros/views/ManageNetwork/queries";
import { queries as proProfileQueries } from "domains/pros/views/ProProfile/queries";
import { getFeatureFlag } from "providers/LaunchDarkly/client";
import { warmQueries } from "routing/warmQueries";
import * as z from "zod/mini";
import { authedRoute } from "./authed";
import { locationLayoutRoute } from "./locations";

// The flag decides whether the group lists are narrowed to a source, so the warmed key has to match what the containers read
const oneKindOfGroup = (queryClient: QueryClient) => getFeatureFlag("jobs-in-main-menu", { queryClient });

// Splat multiplexer under authedRoute (not signedUpRoute): ConnectLimited switches
// list vs pro-profile on `params._splat`.
export const connectLimitedRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "connect/$",
  validateSearch: z.extend(connectSearchSchema, {
    signup: z._default(z.boolean(), true),
    market_id: z.optional(z.number()),
    pro_uuid: z.optional(z.string()),
  }),
  loaderDeps: ({ search: { signup: _signup, ...search } }) => search,
  loader: ({ params, deps, context }) =>
    warmQueries(
      context.queryClient,
      limitedQueries({ splat: params._splat, search: deps, isAdmin: context.auth.user.is_gigpro_admin }),
    ),
  component: lazyRouteComponent(() => import("domains/connect/views/Limited"), "ConnectLimited"),
});

// Pathless guard so Connect stays LM-only (OSMs were redirected off it in the old lmOnlyLoader tree).
// It adds no path segment, so the leaves keep their `connect/*` fullPaths and the CONNECT->PROS
// redirect stays a sibling leaf, not an ancestor.
const connectGuardRoute = createRoute({
  getParentRoute: () => locationLayoutRoute,
  id: "connectGuard",
  beforeLoad: ({ context, params }) => requireLocationManager(context.auth.user, Number(params.locationId)),
});

const connectRedirectRoute = createRoute({
  getParentRoute: () => connectGuardRoute,
  path: "connect",
  beforeLoad: ({ params }) => {
    throw redirect({ to: "/locations/$locationId/connect/pros", params: { locationId: params.locationId } });
  },
});

const connectProsRoute = createRoute({
  getParentRoute: () => connectGuardRoute,
  path: "connect/pros",
  validateSearch: connectSearchSchema,
  loaderDeps: ({ search }) => search,
  loader: async ({ deps, context }) => {
    if (!context.location) return;
    const allSources = await oneKindOfGroup(context.queryClient);
    warmQueries(
      context.queryClient,
      connectQueries({ tab: "pros", search: deps, location: context.location, allSources }),
    );
  },
  component: lazyRouteComponent(() => import("domains/pros/views/Connect"), "ConnectFull"),
});

export const connectProsProfileRoute = createRoute({
  getParentRoute: () => connectGuardRoute,
  path: "connect/pros/$proId",
  validateSearch: directOfferSearchSchema,
  loader: ({ params, context }) =>
    warmQueries(
      context.queryClient,
      proProfileQueries({ proId: Number(params.proId), isAdmin: context.auth.user.is_gigpro_admin }),
    ),
  component: lazyRouteComponent(() => import("domains/pros/views/ProProfilePage"), "ProProfilePage"),
});

// Hire's Candidates list, narrowed to the pros who applied, for whoever sees Jobs once it is in the main menu
export const connectApplicantsRoute = createRoute({
  getParentRoute: () => connectGuardRoute,
  path: "connect/applicants",
  // Same schema as Hire's Candidates so the one list reads either route
  validateSearch: candidatesSearchSchema,
  beforeLoad: async ({ context: { auth, queryClient, location }, params, search }) => {
    const [jobsInMainMenu, jobsPhased] = await Promise.all([
      getFeatureFlag("jobs-in-main-menu", { queryClient }),
      getFeatureFlag("jobs-phased", { queryClient }),
    ]);
    const hasAccess =
      !!location &&
      hasJobsAccess({
        jobsPhased,
        jobsEnabled: location.jobs_enabled,
        user: auth.user,
        locationId: Number(params.locationId),
      });
    if (!jobsInMainMenu || !hasAccess) {
      throw redirect({ to: "/locations/$locationId/connect/pros", params: { locationId: params.locationId } });
    }
    // This view has no tabs, so a hand-typed tab or group_id would only change what the list asks for
    if (search.tab !== undefined || search.group_id !== undefined) {
      const { tab: _tab, group_id: _group_id, ...rest } = search;
      throw redirect({
        to: "/locations/$locationId/connect/applicants",
        params: { locationId: params.locationId },
        search: rest,
      });
    }
  },
  // offerType/candidate_id/applicant_id drive the sheet UI only, so a card-select doesn't re-run the list fetch.
  loaderDeps: ({
    search: { offerType: _offerType, candidate_id: _candidate_id, applicant_id: _applicant_id, ...rest },
  }) => rest,
  // A location without a Jobs plan gets only its top applicants from the list, so it warms like any other.
  loader: ({ deps, context }) =>
    warmQueries(context.queryClient, candidatesQueries({ search: deps, appliedOnly: true })),
  component: lazyRouteComponent(() => import("domains/pros/views/Connect"), "ConnectFull"),
});

const connectGroupsRoute = createRoute({
  getParentRoute: () => connectGuardRoute,
  path: "connect/groups",
  loader: async ({ context }) => {
    if (!context.location) return;
    const allSources = await oneKindOfGroup(context.queryClient);
    warmQueries(context.queryClient, connectQueries({ tab: "groups", location: context.location, allSources }));
  },
  component: lazyRouteComponent(() => import("domains/pros/views/Connect"), "ConnectFull"),
});

const connectGroupsDetailRoute = createRoute({
  getParentRoute: () => connectGuardRoute,
  path: "connect/groups/$groupId",
  validateSearch: directOfferSearchSchema,
  loader: async ({ context }) => {
    if (!context.location) return;
    const allSources = await oneKindOfGroup(context.queryClient);
    warmQueries(context.queryClient, connectQueries({ tab: "groupDetail", location: context.location, allSources }));
  },
  component: lazyRouteComponent(() => import("domains/pros/views/Connect"), "ConnectFull"),
});

const connectNetworkRoute = createRoute({
  getParentRoute: () => connectGuardRoute,
  path: "connect/network",
  loader: async ({ context }) => {
    if (!context.location) return;
    const allSources = await oneKindOfGroup(context.queryClient);
    warmQueries(context.queryClient, connectQueries({ tab: "network", location: context.location, allSources }));
  },
  component: lazyRouteComponent(() => import("domains/pros/views/Connect"), "ConnectFull"),
});

const connectNetworkInvitesRoute = createRoute({
  getParentRoute: () => connectGuardRoute,
  path: "connect/network/invites",
  loader: ({ context }) =>
    context.location &&
    warmQueries(context.queryClient, manageNetworkQueries({ orgId: context.location.organization_id })),
  component: lazyRouteComponent(() => import("domains/pros/views/ManageNetwork"), "ManageNetwork"),
});

export const connectLocationRoutes = connectGuardRoute.addChildren([
  connectRedirectRoute,
  connectProsRoute,
  connectProsProfileRoute,
  connectApplicantsRoute,
  connectGroupsRoute,
  connectGroupsDetailRoute,
  connectNetworkRoute,
  connectNetworkInvitesRoute,
]);
